import {
  useState,
  useEffect,
  useCallback,
  useContext,
  createContext,
  type ReactNode,
} from 'react';
import { getToken, setToken as saveToken, clearToken, clearAllData } from '../lib/storage';
import { validateToken } from '../lib/api';
import { initTelemetry, track } from '../lib/telemetry';

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string) => Promise<boolean>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check authentication on mount
  useEffect(() => {
    async function checkAuth() {
      try {
        const token = await getToken();
        if (!token) {
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        // Validate token with server
        // Returns: true = valid, false = invalid (401), null = network error/timeout
        const validationResult = await validateToken();

        if (validationResult === false) {
          // Token is definitely invalid (401) - clear it
          await clearToken();
          setIsAuthenticated(false);
        } else {
          // Token is valid OR network error (assume valid, let API calls fail later)
          setIsAuthenticated(true);
          // Best-effort telemetry boot for reloaded sessions.
          void initTelemetry();
        }
      } catch (error) {
        console.error('Auth check error:', error);
        // On unexpected errors, assume authenticated if we have a token
        const token = await getToken();
        setIsAuthenticated(!!token);
      } finally {
        setIsLoading(false);
      }
    }

    checkAuth();
  }, []);

  const login = useCallback(async (token: string): Promise<boolean> => {
    // Save token temporarily
    await saveToken(token);

    // Validate with server
    // For login, we require a definitive valid response (not network error)
    const validationResult = await validateToken();

    if (validationResult === true) {
      setIsAuthenticated(true);
      // Re-try telemetry init now that we have a valid token. The main.tsx
      // boot call was a no-op if we booted unauthenticated.
      void initTelemetry();
      track('auth_connected');
      return true;
    } else {
      // Remove invalid token (or couldn't validate due to network)
      await clearToken();
      track('auth_failed', {
        reason: validationResult === false ? 'invalid_token' : 'network_error',
      });
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await clearToken();
      await clearAllData();
    } catch (err) {
      console.error('Error clearing data:', err);
    }
    window.location.href = '/';
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
